import React from 'react';
import { HardDrive, Camera, Clock, ShieldCheck, RefreshCw, FolderOpen } from 'lucide-react';

function Step({ n, icon: Icon, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <span className="w-8 h-8 bg-primary/10 border border-primary/30 text-primary font-black text-xs flex items-center justify-center shrink-0">
          {n}
        </span>
        <div className="w-px flex-1 bg-white/5 mt-1" />
      </div>
      <div className="pb-5">
        <div className="flex items-center gap-2 mb-1.5">
          {Icon && <Icon className="w-4 h-4 text-primary" />}
          <h4 className="text-white font-bold text-sm">{title}</h4>
        </div>
        <div className="text-white/55 text-xs leading-relaxed space-y-1.5">{children}</div>
      </div>
    </div>
  );
}

function Cmd({ children }) {
  return <code className="text-white/80 bg-[#0d0d0f] border border-white/5 px-1.5 py-0.5 text-[11px]">{children}</code>;
}

export default function SynologyBackupGuide() {
  return (
    <div className="bg-[#111115] border border-white/5 p-6">
      <div className="flex items-center gap-3 mb-1">
        <HardDrive className="w-5 h-5 text-primary" />
        <h2 className="text-white font-black text-lg uppercase">Synology NAS Backup Guide</h2>
      </div>
      <p className="text-white/40 text-xs mb-5 max-w-2xl">
        The sync (above) keeps a live <span className="text-white/70">motion_reality.db</span> on the NAS, with real named columns and built-in report views. This guide turns that live mirror into versioned, automated backups — so you can roll back to any day and never lose data if the website is wiped.
      </p>

      <div className="bg-primary/5 border border-primary/20 p-3.5 mb-6 flex items-start gap-2.5">
        <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <p className="text-white/65 text-xs leading-relaxed">
          Recommended setup: <span className="text-white font-bold">Live mirror</span> (Docker container) → <span className="text-white font-bold">Daily timestamped snapshot</span> (Task Scheduler) → <span className="text-white font-bold">Offsite copy</span> (Hyper Backup to Backblaze B2 or a second drive). Three layers, zero manual work.
        </p>
      </div>

      <Step n={1} icon={FolderOpen} title="Create a dedicated shared folder">
        <p>Open <span className="text-white">Control Panel → Shared Folder → Create</span> and name it <Cmd>motion_reality_db</Cmd>.</p>
        <p>Give read/write access to your admin account. This folder will hold the live <Cmd>motion_reality.db</Cmd> from the Docker sync, plus a <Cmd>/backups</Cmd> subfolder for snapshots.</p>
      </Step>

      <Step n={2} icon={RefreshCw} title="Point the sync container at the shared folder">
        <p>In the <span className="text-white">docker-compose.yml</span> from the Docker bundle, set the volume so the database lands in the shared folder:</p>
        <pre className="bg-[#0d0d0f] border border-white/5 p-3 text-white/70 text-[11px] overflow-x-auto mt-1.5"><code>{`volumes:
  - /volume1/motion_reality_db:/data`}</code></pre>
        <p>Rebuild &amp; start: <Cmd>docker compose up -d --build</Cmd>. The live database now refreshes every sync interval at <Cmd>/volume1/motion_reality_db/motion_reality.db</Cmd>.</p>
      </Step>

      <Step n={3} icon={Clock} title="Schedule a daily timestamped snapshot">
        <p>Open <span className="text-white">Control Panel → Task Scheduler → Create → Scheduled Task → User-defined script</span>. Set it to run <span className="text-white">daily at 02:00</span> as your admin user, with this script:</p>
        <pre className="bg-[#0d0d0f] border border-white/5 p-3 text-white/70 text-[11px] overflow-x-auto mt-1.5"><code>{`#!/bin/bash
DB=/volume1/motion_reality_db/motion_reality.db
DEST=/volume1/motion_reality_db/backups
mkdir -p "$DEST"
# Safe copy: SQLite hot backup while the sync is writing
sqlite3 "$DB" ".backup '$DEST/motion_reality_$(date +%Y-%m-%d_%H%M).db'"
# Keep the last 30 daily snapshots, remove the rest
ls -1t "$DEST"/motion_reality_*.db | tail -n +31 | xargs -r rm -f`}</code></pre>
        <p>This uses SQLite's own hot-backup command so the copy is never corrupt, and it automatically deletes snapshots older than 30 days.</p>
        <p className="text-white/35">If <Cmd>sqlite3</Cmd> isn't installed on the NAS, use <Cmd>cp "$DB" "$DEST/motion_reality_$(date +%F).db"</Cmd> instead — still safe enough for a low-traffic site outside business hours.</p>
      </Step>

      <Step n={4} icon={Camera} title="Turn on Snapshot Protection (Btrfs only)">
        <p>If your volume is formatted as <span className="text-white">Btrfs</span>, open <span className="text-white">Snapshot Replication → Create → Snapshot</span> for the <Cmd>motion_reality_db</Cmd> shared folder. Schedule hourly snapshots with a 7-day retention.</p>
        <p>This gives you point-in-time recovery independent of the daily script — handy if someone deletes records mid-day.</p>
      </Step>

      <Step n={5} icon={ShieldCheck} title="Add offsite backup with Hyper Backup">
        <p>Open <span className="text-white">Hyper Backup → Create → Data backup task</span>. Select the <Cmd>motion_reality_db</Cmd> shared folder (or just the <Cmd>/backups</Cmd> subfolder) as the source.</p>
        <p>Destination: <span className="text-white">Backblaze B2</span> (cheapest, ~£0.005/GB/mo), a second Synology, or an external USB drive. Schedule daily, enable client-side encryption, and set retention to keep the last 90 versions.</p>
        <p>Hyper Backup deduplicates and compresses, so a year of daily DBs costs pennies. This is your protection against the NAS itself failing or being stolen.</p>
      </Step>

      <Step n={6} icon={FolderOpen} title="Open the backup from any PC">
        <p>Map <Cmd>\\<span className="opacity-70">NAS-IP</span>\motion_reality_db</Cmd> as a network drive on any office PC, or browse it in <span className="text-white">File Station</span>. Open any snapshot in <span className="text-white">DB Browser for SQLite</span> to inspect or restore records — no import step needed.</p>
        <p>Each table has real named columns you can sort &amp; filter, plus ready-made report views under <span className="text-white">Browse Data → Table</span> — try <Cmd>v_low_stock</Cmd>, <Cmd>v_top_customers</Cmd>, <Cmd>v_orders_by_status</Cmd>, <Cmd>v_bookings_by_rig</Cmd> or <Cmd>v_gift_cards_outstanding</Cmd>.</p>
        <p>To restore the live site is out of scope here (the website DB is managed by the platform), but having the full nightly snapshot means you always know exactly what every record looked like on any date.</p>
      </Step>
    </div>
  );
}